import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NearestBillboard } from '@/features/billboards/api/billboardApi';
import { getGoogleMapsApiKey, formatScreenName } from '@/features/billboards/utils/billboardDetailUtils';
import { routes } from '@/shared/config/routes';
import {
  createScreenistaMarkerIcon,
  getBillboardCoordinates,
  getMarkerIconConfig,
  getUserLocationIcon,
  loadGoogleMapsScript,
} from '@/shared/lib/maps/googleMaps';

type Coordinates = { lat: number; lng: number };

type NearbyBillboardsMapProps = {
  userLocation: Coordinates;
  billboards: NearestBillboard[];
  radiusKm: number;
  isLoading: boolean;
  onRecenter: () => void;
};

export function NearbyBillboardsMap({
  userLocation,
  billboards,
  radiusKm,
  isLoading,
  onRecenter,
}: NearbyBillboardsMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const navigate = useNavigate();
  const [mapError, setMapError] = useState(false);
  const [mapTypeId, setMapTypeId] = useState<'roadmap' | 'satellite'>('roadmap');

  const handleMapTypeChange = (type: 'roadmap' | 'satellite') => {
    setMapTypeId(type);
    mapInstanceRef.current?.setMapTypeId(type);
  };

  useEffect(() => {
    const apiKey = getGoogleMapsApiKey();
    if (!apiKey || !mapRef.current) {
      if (!apiKey) setMapError(true);
      return;
    }

    let isMounted = true;
    let radiusCircle: google.maps.Circle | null = null;
    let userMarker: google.maps.Marker | null = null;
    let screenMarkers: google.maps.Marker[] = [];

    loadGoogleMapsScript(apiKey)
      .then(async (googleMaps) => {
        if (!mapRef.current || !isMounted) return;

        const markerIconUrl = await createScreenistaMarkerIcon();

        const map = new googleMaps.Map(mapRef.current, {
          center: userLocation,
          zoom: 13,
          mapTypeId: mapTypeId,
          disableDefaultUI: true,
        });
        mapInstanceRef.current = map;

        radiusCircle = new googleMaps.Circle({
          strokeColor: '#8156B4',
          strokeOpacity: 0.35,
          strokeWeight: 2,
          fillColor: '#8156B4',
          fillOpacity: 0.08,
          map,
          center: userLocation,
          radius: radiusKm * 1000,
        });

        userMarker = new googleMaps.Marker({
          position: userLocation,
          map,
          title: 'Your location',
          icon: getUserLocationIcon(googleMaps),
          zIndex: 999,
        });

        const bounds = new googleMaps.LatLngBounds();
        bounds.extend(userLocation);

        billboards.forEach((billboard) => {
          const coords = getBillboardCoordinates(billboard);
          if (!coords) return;

          const marker = new googleMaps.Marker({
            position: coords,
            map,
            title: formatScreenName(billboard?.screenName, 'Screen'),
            icon: getMarkerIconConfig(googleMaps, markerIconUrl),
          });

          marker.addListener('click', () => {
            const screenId = billboard?._id;
            if (screenId) {
              navigate(routes.screenDetail(screenId), {
                state: { from: routes.nearbyScreens },
              });
            }
          });

          screenMarkers.push(marker);
          bounds.extend(coords);
        });

        if (screenMarkers.length > 0) map.fitBounds(bounds, 64);
      })
      .catch((err) => {
        console.error('Error loading nearby billboards map', err);
        if (isMounted) setMapError(true);
      });

    return () => {
      isMounted = false;
      mapInstanceRef.current = null;
      radiusCircle?.setMap(null);
      userMarker?.setMap(null);
      screenMarkers.forEach((marker) => marker.setMap(null));
      screenMarkers = [];
    };
  }, [userLocation, billboards, radiusKm, navigate]);

  /* Modified by: Ishan | Soft-contrast nearby map error and container */
  if (mapError) {
    return (
      <div className="flex h-[480px] sm:h-[520px] w-full items-center justify-center rounded-[24px] border border-[#EEEBF8] bg-[#F3F2F9] text-sm font-medium text-[#6A6688]">
        Failed to load map. Please check your API key.
      </div>
    );
  }

  /* Modified by: Ishan | Soft-contrast nearby map container with map controls and recenter button */
  return (
    <div className="relative h-[480px] sm:h-[520px] w-full overflow-hidden rounded-[24px] shadow-[0_10px_30px_rgba(60,40,120,0.05)] border border-[#EEEBF8] bg-[#EAEFE9]">
      <div className="absolute top-3.5 left-3.5 z-20 flex overflow-hidden rounded-[12px] bg-white text-[13px] font-bold shadow-md">
        <button
          type="button"
          onClick={() => handleMapTypeChange('roadmap')}
          className={`cursor-pointer border-none px-4 py-2 transition ${
            mapTypeId === 'roadmap'
              ? 'bg-[#EDE4F8] text-[#2B3E8A]'
              : 'bg-transparent text-[#6A6688] hover:text-[#3A3660]'
          }`}
        >
          Map
        </button>
        <button
          type="button"
          onClick={() => handleMapTypeChange('satellite')}
          className={`cursor-pointer border-none px-4 py-2 transition ${
            mapTypeId === 'satellite'
              ? 'bg-[#EDE4F8] text-[#2B3E8A]'
              : 'bg-transparent text-[#6A6688] hover:text-[#3A3660]'
          }`}
        >
          Satellite
        </button>
      </div>

      <button
        type="button"
        onClick={onRecenter}
        className="absolute top-3.5 right-3.5 z-20 cursor-pointer rounded-[12px] bg-white px-4 py-2 text-[13px] font-bold text-[#3A3660] shadow-md transition hover:bg-[#F3F2F9]"
      >
        Recenter
      </button>

      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#8156B4]/20 border-t-[#8156B4]" />
            <p className="text-sm font-semibold text-[#6A6688]">
              Loading nearby screens...
            </p>
          </div>
        </div>
      ) : null}

      <div ref={mapRef} className="h-full w-full" />
    </div>
  );
}
